<template>
  <Form
    class="w-full flex flex-col gap-4"
    @submit="onSubmit">
    <TextInputField
      v-if="vetting.vatNumber"
      :label="t('vetting.fields.vatNumber.label')"
      label-position="top"
      name="vatNumber"
      :placeholder="t('vetting.fields.vatNumber.placeholder')" />

    <TextInputField
      v-if="vetting.routingNumber"
      :label="t('vetting.fields.routingNumber.label')"
      label-position="top"
      name="routingNumber"
      :placeholder="t('vetting.fields.routingNumber.placeholder')" />

    <TextInputField
      v-if="vetting.pecEmail"
      :label="t('vetting.fields.pecEmail.label')"
      label-position="top"
      name="pecEmail"
      :placeholder="t('vetting.fields.pecEmail.placeholder')" />

    <Paragraph
      v-if="vettingError"
      class="text-red text-sm">
      {{ t(vettingError) }} {{ t('vetting.errors.checkDetails') }}
    </Paragraph>

    <FormRow class="justify-center mt-3">
      <ButtonWithSpinner
        :disabled="!meta.dirty"
        :is-loading="isSubmitting"
        type="submit"
        @click="clearVettingError()">
        {{ t('vetting.submit') }}
      </ButtonWithSpinner>
    </FormRow>
  </Form>
</template>

<script setup lang="ts">
import type {ApiException} from '@myparcel/sdk';
import type {Shop} from '@myparcel-frontend/types';
import {TextInputField, Form} from '@myparcel-frontend/forms';
import {useNotification} from '@myparcel-frontend/notifications';
import {useProposition} from '@myparcel-frontend/proposition';
import {VettingStatus} from '@myparcel-frontend/types';
import {ButtonWithSpinner, FormRow, Paragraph} from '@myparcel-frontend/ui';
import {regex} from '@myparcel-frontend/utils';
import {attemptAsync} from 'es-toolkit';
import {camelCase} from 'es-toolkit/string';
import {useForm} from 'vee-validate';
import {computed, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {z} from 'zod';
import useAccount from '../../../composables/useAccount/useAccount';
import useShop from '../../../composables/useShop/useShop';
import {getErrorTranslationKey} from '@/utils/api/getErrorTranslationKey';

const emit = defineEmits<{
  success: [];
}>();

const {t, te} = useI18n();
const {currentShop, updateShop, fetchShop} = useShop();
const {currentUser} = useAccount();
const {addNotification} = useNotification();

const {proposition} = useProposition();
const {vetting} = proposition.value.billing;
const isSubmitting = ref(false);
const vettingError = ref('');

const getFailureReason = () => {
  const reason = currentShop.value?.vetting?.failure_reason;

  if (!reason) {
    return '';
  }

  const errorKey = `vetting.errors.${camelCase(reason)}`;
  return te(errorKey) ? errorKey : 'vetting.errors.generic';
};

vettingError.value = getFailureReason();

const validationSchema = computed(() => {
  return z.object({
    // Each field is optional and enabled through the proposition config.
    ...(vetting.vatNumber && {
      vatNumber: z
        .string()
        .min(1, t('vetting.fields.vatNumber.validation.required'))
        // TODO: CNP-1176 - regex from centralised validation lib (or helper)
        .regex(/^IT\d{11}$/i, t('vetting.fields.vatNumber.validation.invalid')),
    }),
    ...(vetting.routingNumber && {
      routingNumber: z
        .string()
        .min(1, t('vetting.fields.routingNumber.validation.required'))
        .regex(/^[A-Za-z0-9]{7}$/, t('vetting.fields.routingNumber.validation.invalid')),
    }),
    ...(vetting.pecEmail && {
      pecEmail: z
        .string()
        .min(1, t('vetting.fields.pecEmail.validation.required'))
        .regex(regex.email, t('vetting.fields.pecEmail.validation.invalid'))
        .refine(
          (val) => val.trim().toLowerCase() !== currentUser.value!.contact.email.toLowerCase(),
          t('vetting.fields.pecEmail.validation.sameAsRegistration'),
        ),
    }),
  });
});

const {handleSubmit, meta} = useForm({
  validationSchema,
  initialValues: {
    ...(vetting.vatNumber && {vatNumber: currentShop.value?.billing?.vat_number ?? ''}),
    ...(vetting.routingNumber && {routingNumber: currentShop.value?.billing?.routing_number ?? ''}),
    ...(vetting.pecEmail && {pecEmail: currentShop.value?.billing?.pec_email ?? ''}),
  },
});

const clearVettingError = (): void => {
  vettingError.value = '';
};

/**
 * API throws only human-readable error messages, so we need to parse the message to get the error key.
 * Example: "Vetting failed, reason: vat_number_invalid (request_id: 1776777714.25569e779f23e3f1)"
 */
const getErrorKeyFromVettingFailedReasonPayload = (error: Error): string | undefined => {
  // Find if the error message contains a reason:
  if (!error.message.includes('reason:')) {
    return;
  }

  return camelCase(error.message.split('reason:')[1].trim().split(' ')[0]);
};

const onSubmit = handleSubmit(async (values) => {
  vettingError.value = '';
  isSubmitting.value = true;

  const updatedShop: Shop = {
    ...currentShop.value,
    billing: {
      ...currentShop.value.billing,
      ...(vetting.vatNumber && {vat_number: (values.vatNumber as string).trim()}),
      ...(vetting.routingNumber && {routing_number: (values.routingNumber as string).trim()}),
      ...(vetting.pecEmail && {pec_email: (values.pecEmail as string).trim()}),
    },
  };

  const [updateError] = await attemptAsync<unknown, ApiException>(() => updateShop(updatedShop));

  if (updateError) {
    isSubmitting.value = false;

    // Check if API has error code is available in our translations. This is before vetting takes place in the API.
    const code = updateError.data?.errors?.[0]?.code;

    if (code && te(getErrorTranslationKey(code))) {
      vettingError.value = getErrorTranslationKey(code);
      return;
    }

    // This is after vetting has taken place. Vetting returns a human readable string with a parsable error key.
    const key = getErrorKeyFromVettingFailedReasonPayload(updateError);

    if (key && te(`vetting.errors.${key}`)) {
      vettingError.value = `vetting.errors.${key}`;
    } else {
      vettingError.value = 'vetting.errors.generic';
    }

    return;
  }

  // Get the fresh shop object from the API, which contains the updated vetting status in the Shop object.
  const [fetchError] = await attemptAsync(() => fetchShop(currentShop.value.id));

  isSubmitting.value = false;

  if (fetchError) {
    vettingError.value = 'vetting.errors.generic';
    return;
  }

  if (currentShop.value.vetting?.status !== VettingStatus.Passed) {
    const failureKey = `vetting.errors.${camelCase(currentShop.value.vetting?.failure_reason ?? '')}`;

    // If failure reason key from the API matches an available translation in the frontend.
    if (te(failureKey)) {
      vettingError.value = failureKey;
      return;
    }

    vettingError.value = 'vetting.errors.generic';
    return;
  }

  // All went well. Show notification and close modal.
  addNotification({
    variant: 'success',
    body: t('vetting.update.success'),
  });

  emit('success');
});
</script>
